import { formatDate } from '../../../libs/date';
import { parseError } from '../../../libs/parser';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CommonDescription from '../../../components/CommonDescription';
import Context from '../../../provider';
import ErrorIcon from '@mui/icons-material/Error';
import IconBenefitsRed from '../../../assets/hkdc/icons/BenefitsRedIcon';
import IconEdit from '../../../assets/hkdc/icons/EditIcon';
import IconMembershipProfile from '../../../assets/hkdc/icons/MembershipProfileIcon';
import IconRenewPayment from '../../../assets/hkdc/icons/RenewPaymentIcon';
import IconVoucher from '../../../assets/hkdc/icons/VoucherIcon';
import IconVoucherNone from '../../../assets/hkdc/icons/VoucherNoneIcon';
import LogoFooterComponent from '../../../components/LogoFooterComponent';
import PageTitleContComponent from '../../../components/PageTitleContComponent';
import React, { useContext, useEffect, useRef, useState } from 'react';
import config from '../../../config';
import memberApi from '../../../libs/api/memberApi';
import useIsScrolling from '../../../hooks/useIsScrolling';
import useNotificationsCount from '../../../hooks/useNotificationsCount';
import * as dayjs from 'dayjs';
import './index.scss';

export default function WebMembershipProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const notificationNotReadCount = useNotificationsCount();

  const scrollRef = useRef();
  const isScrolling = useIsScrolling(scrollRef.current);

  const { init, dispatch, tierMission, me, lang } = useContext(Context);

  const [tierContentData, setTierContentData] = useState({});
  const [vouchersData, setVouchersData] = useState([]);
  const [pageData, setPageData] = useState(null);

  const getItem = async (page) => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const response = await memberApi.getItems({
        page,
        pageSize: 1000,
        status: 'unavailable'
      });
      setVouchersData((prevData) => [...prevData, ...(response?.list ?? []).filter((i) => !(prevData.find((p) => p.id === i.id)))]);
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  const fetchTierContentData = async () => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const tierType = me.loyalty_account?.tier?.code.replace('tier-', '');
      const page = await memberApi.getPage({ ruri: `${tierType}-benefits` });
      setTierContentData(page);
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  const fetchDescData = async () => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const page = await memberApi.getPage({ ruri: 'profile-header' });
      setPageData(page);
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  // This page only allow logged in user
  useEffect(() => {
    if (init) {
      getItem(1);
      fetchTierContentData();
      fetchDescData();

      console.log('Mobile Membership Profile page', dayjs(tierMission?.expire_at).diff(dayjs(), 'day'));

    }
  }, [init, me, navigate]);

  return (
    (
      <div className='web-membership-profile-page'>
        <div ref={scrollRef} className="y-overflow gray-block content-container">

          <div className="black-block full-width">
            <PageTitleContComponent
              titleText={pageData?.sections[0].title[lang] ?? ''}
              descText={<CommonDescription data={pageData?.sections[0].content[lang] ?? ''} />}
              pageTitleContStyle={{
                padding: '1.875rem 0rem'
              }}
              titleContStyle={{
                gap: '2.5rem'
              }}
              pageTitleIcon={
                <IconMembershipProfile
                style={{
                  width: '21.41rem',
                  height: '18.75rem'
                }}
                  size="large" />
              }
            />
          </div>

          <div className="section-max-container section" >
            <div className="white-block inner-container">
              <div className="membership-info-container">
                <div className="title-btn-cont">
                  <div className="bar-title-container">
                    <IconBenefitsRed
                      className="icon"
                      size="large" />
                    <div className="title">
                      {t('profile.profile-title')}
                    </div>
                  </div>
                  <div className="edit-btn-container"
                    style={{ backgroundColor: '#004899' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`${config.basename}setting/profile/user`);
                    }}
                  >
                    <div className="text-item">
                      {t('profile.btn')}
                    </div>
                    <IconEdit
                      className="icon"
                      size="large" />
                  </div>
                </div>

                <div className="info-content-container">
                  <div className="type-content-container">
                    <div className="title">
                      {t('profile.name')}
                    </div>
                    {
                      me && me.loyalty_account &&
                      <div className="content">
                        {`${me.loyalty_account?.first_name} ${me.loyalty_account?.last_name}`}
                      </div>
                    }
                  </div>
                  <div className="tier-info-cont">
                    <div className="type-content-container">
                      <div className="title">
                        {t('profile.membership')}
                      </div>
                      {
                        me && me.loyalty_account &&
                        <div className="content">
                          {me.loyalty_account?.tier?.title?.[lang]}
                        </div>
                      }
                      {
                        me && me.loyalty_account?.tier_renewal &&
                        tierMission?.expire_at && me.loyalty_account?.tier_renewal.expire_from_now <= 30 &&
                        <div className="info-text-cont">
                          <ErrorIcon />
                          <div className="desc">
                            {`${(t('profile.expiry')).replace('{{date}}', formatDate(tierMission?.expire_at, lang))}`}
                          </div>
                        </div>
                      }
                    </div>

                    {
                      me && me.loyalty_account?.tier_renewal &&
                      tierMission?.expire_at && me.loyalty_account?.tier_renewal.expire_from_now <= 30 &&
                      <div className="edit-btn-container"
                        style={{ backgroundColor: '#000000' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`${config.basename}renewal`);
                        }}
                      >
                        <div className="text-item">
                          {t('home-after.shortcut-3')}
                        </div>
                        <IconRenewPayment
                          className="icon"
                          size="large" />
                      </div>
                    }
                  </div>

                  <div className="type-content-container">
                    <div className="title">
                      {t('profile.email')}
                    </div>
                    {
                      me &&
                      <div className="content">
                        {me.email}
                      </div>
                    }
                  </div>
                </div>
              </div>

              <div className="benefits-detail-container">
                <div className="title-cont">
                  {
                    me && me.loyalty_account &&
                    <div className="title">
                      {`${t('profile.tier-title')} - ${me.loyalty_account?.tier?.title?.[lang]}`}
                    </div>
                  }
                </div>
                <div className='list-cont'>
                  {
                    tierContentData.sections && tierContentData.sections.length > 0 && tierContentData.sections.map((item, index) => {
                      return (
                        <div key={index} className='item'>
                          <div className='item-img' style={{ backgroundImage: `url(${item.image[lang]})` }} />
                          <div className='item-content'>
                            <div className='item-text-title'>
                              {item.title[lang]}
                            </div>
                            <div className='item-text-desc'>
                              <CommonDescription data={item.content[lang]} />
                            </div>
                          </div>
                        </div>
                      );
                    })
                  }
                </div>
              </div>

              <div className="used-vouchers-list-container">
                <div className="bar-title-container">
                  <IconBenefitsRed
                    className="icon"
                    size="large" />
                  <div className="title">
                    {t('profile.voucher-title')}
                  </div>
                </div>
                <div className="voucher-list-container">
                  {
                    vouchersData.length > 0 && vouchersData.map((item, index) => {
                      return (
                        <div key={index} className="content-item-cont">
                          <div className="item-icon-title">
                            <div className="icon-cont">
                              <IconVoucher
                                className="icon"
                                size="large" />
                            </div>
                            <div className="title-cont">
                              <div className="title">
                                {item.title[lang]}
                              </div>
                              <div className="time">
                                {`${t('home-after.coupon-expiry-1')}${formatDate(item.expire_at, lang)}`}
                              </div>
                            </div>
                          </div>
                          <div className="item-btn-cont">
                            <div className="btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`${config.basename}voucher/${item.token}`, {
                                  state: {
                                    data: item.token
                                  }
                                });
                              }}
                            >
                              <div className="text">
                              { item.usage?.percentage === 100 ? t('profile.voucher-used') : t('profile.voucher-expired') }
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  }
                  {
                    (!vouchersData || vouchersData.length === 0) &&
                    <div className="voucher-list-none">
                      <IconVoucherNone
                        className="icon"
                        size="large" />
                      <div className="text">
                        {t('none-data.coupon')}
                      </div>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>

          <div className="black-block full-width">
            <LogoFooterComponent />
          </div>
        </div>
      </div>
    )
  );
}